import type { Source } from '../services/abrangencia';

export interface AbrangenciaTuple {
  search: string;
  platform: string;
  query: string;
  key: string;
}

export interface StatusAbrangenciaRow extends AbrangenciaTuple {
  value: string | null;
}

export interface AbrangenciaCompareResult {
  productionTotal: number;
  statusTotal: number;
  missingInStatus: AbrangenciaTuple[];
  extraInStatus: AbrangenciaTuple[];
  notDisplayed: AbrangenciaTuple[];
}

export type OutdatedDivergenceType = 'missingInStatus' | 'extraInStatus';

export interface OutdatedDivergenceRow extends AbrangenciaTuple {
  divergenceType: OutdatedDivergenceType;
}

/** Plataformas excluídas da verificação de sincronização de abrangência */
export const IGNORED_SYNC_PLATFORMS = new Set(['pje-jt', 'pje-jt-web']);

export const isIgnoredSyncPlatform = (platform: string): boolean =>
  IGNORED_SYNC_PLATFORMS.has(platform);

/** Chave composta para identificar uma abrangência de forma única */
export const buildAbrangenciaKey = (row: AbrangenciaTuple): string =>
  [row.search, row.platform, row.query, row.key].join('::');

/**
 * Transforma a hierarquia de produção (/available) em tuplas planas.
 * Consultas sem parâmetros são ignoradas; key = param.tag.
 */
export const flattenProductionAbrangencias = (sources: Source[]): AbrangenciaTuple[] => {
  const rows: AbrangenciaTuple[] = [];

  sources.forEach((source) => {
    source.platforms.forEach((platform) => {
      platform.searches.forEach((search) => {
        search.queries.forEach((query) => {
          if (query.params.length === 0) return;

          query.params.forEach((param) => {
            rows.push({
              search: search.search,
              platform: platform.platform,
              query: query.query,
              key: param.tag,
            });
          });
        });
      });
    });
  });

  return rows;
};

/** Indica se há divergência de conjunto entre produção e status */
export const hasOutdatedDivergence = (
  result: Pick<AbrangenciaCompareResult, 'missingInStatus' | 'extraInStatus'>,
): boolean => result.missingInStatus.length > 0 || result.extraInStatus.length > 0;

/** Une ausentes e extras em uma lista para exibição no alerta de sincronização */
export const buildOutdatedDivergences = (
  result: Pick<AbrangenciaCompareResult, 'missingInStatus' | 'extraInStatus'>,
): OutdatedDivergenceRow[] => {
  const rows: OutdatedDivergenceRow[] = [
    ...result.missingInStatus.map((row) => ({ ...row, divergenceType: 'missingInStatus' as const })),
    ...result.extraInStatus.map((row) => ({ ...row, divergenceType: 'extraInStatus' as const })),
  ];

  rows.sort((a, b) => buildAbrangenciaKey(a).localeCompare(buildAbrangenciaKey(b)));
  return rows;
};

/**
 * Compara abrangências de produção com as ativas no banco de status.
 * - missingInStatus: existem em produção mas não no status (ativa=true)
 * - extraInStatus: existem no status mas não em produção
 * - notDisplayed: existem em ambos, mas value é null no status
 */
export const compareAbrangencias = (
  production: AbrangenciaTuple[],
  status: StatusAbrangenciaRow[],
): AbrangenciaCompareResult => {
  const productionMap = new Map<string, AbrangenciaTuple>();
  production.forEach((row) => {
    if (isIgnoredSyncPlatform(row.platform)) return;
    productionMap.set(buildAbrangenciaKey(row), row);
  });

  const statusMap = new Map<string, StatusAbrangenciaRow>();
  status.forEach((row) => {
    if (isIgnoredSyncPlatform(row.platform)) return;
    statusMap.set(buildAbrangenciaKey(row), row);
  });

  const missingInStatus: AbrangenciaTuple[] = [];
  productionMap.forEach((row, key) => {
    if (!statusMap.has(key)) {
      missingInStatus.push(row);
    }
  });

  const extraInStatus: AbrangenciaTuple[] = [];
  statusMap.forEach((row, key) => {
    if (!productionMap.has(key)) {
      extraInStatus.push({
        search: row.search,
        platform: row.platform,
        query: row.query,
        key: row.key,
      });
    }
  });

  const notDisplayed: AbrangenciaTuple[] = [];
  productionMap.forEach((row, key) => {
    const statusRow = statusMap.get(key);
    if (statusRow && (statusRow.value === null || statusRow.value === undefined)) {
      notDisplayed.push(row);
    }
  });

  missingInStatus.sort((a, b) => buildAbrangenciaKey(a).localeCompare(buildAbrangenciaKey(b)));
  extraInStatus.sort((a, b) => buildAbrangenciaKey(a).localeCompare(buildAbrangenciaKey(b)));
  notDisplayed.sort((a, b) => buildAbrangenciaKey(a).localeCompare(buildAbrangenciaKey(b)));

  return {
    productionTotal: productionMap.size,
    statusTotal: statusMap.size,
    missingInStatus,
    extraInStatus,
    notDisplayed,
  };
};
